import { debug } from "./debug";
import { TruncationError } from "./errors";

// --- JSON EXTRACTION UTILITIES ---

// Detect truncated JSON by counting unbalanced braces/brackets (ignoring those inside strings).
const isTruncatedJSON = (text) => {
  let braces = 0;
  let brackets = 0;
  let inString = false;
  let escaped = false;

  for (const ch of text) {
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === "\\") {
      escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === "{") braces++;
    else if (ch === "}") braces--;
    else if (ch === "[") brackets++;
    else if (ch === "]") brackets--;
  }

  return braces > 0 || brackets > 0;
};

const extractJSON = (text) => {
  debug.group("extractJSON");
  debug.log("Input text length:", text?.length);
  debug.log(
    "Input text (first 500 chars):",
    typeof text === "string" ? text.substring(0, 500) : text,
  );

  if (!text || typeof text !== "string") {
    debug.error("Input is not a string:", typeof text, text);
    debug.groupEnd();
    throw new Error(`extractJSON received ${typeof text} instead of string`);
  }

  // Try multiple extraction patterns
  const patterns = [
    { name: "json code block", regex: /```json\s*([\s\S]*?)\s*```/i },
    { name: "generic code block", regex: /```\s*([\s\S]*?)\s*```/ },
    { name: "raw JSON object", regex: /(\{[\s\S]*\})/ },
  ];

  for (const { name, regex } of patterns) {
    const match = text.match(regex);
    if (match) {
      debug.log(`Pattern "${name}" matched`);
      try {
        const jsonStr = match[1] || match[0];
        debug.log(
          "Extracted JSON (first 200 chars):",
          jsonStr.substring(0, 200),
        );
        const parsed = JSON.parse(jsonStr);
        debug.log(
          "Successfully parsed JSON. Top-level keys:",
          Object.keys(parsed),
        );
        debug.groupEnd();
        return parsed;
      } catch (e) {
        debug.warn(
          `Pattern "${name}" matched but JSON parse failed:`,
          e.message,
        );
        // If the extracted text has unbalanced braces, it's truncated — fail fast
        const extracted = match[1] || match[0];
        if (isTruncatedJSON(extracted)) {
          debug.error("Detected truncated JSON within matched pattern");
          debug.groupEnd();
          throw new TruncationError(
            `Response truncated within ${name}. Length: ${extracted.length} chars`,
            { rawText: extracted },
          );
        }
        continue;
      }
    }
  }

  // Last resort: try parsing the entire text
  try {
    debug.log("Trying to parse entire text as JSON");
    const parsed = JSON.parse(text);
    debug.log("Direct parse succeeded. Top-level keys:", Object.keys(parsed));
    debug.groupEnd();
    return parsed;
  } catch (e) {
    debug.error("All extraction methods failed");
    debug.error("Full text:", text);
    debug.groupEnd();

    // Distinguish truncated JSON from genuinely malformed responses
    if (isTruncatedJSON(text)) {
      throw new TruncationError(
        `Response appears truncated (unbalanced JSON). Length: ${text.length} chars`,
        { rawText: text },
      );
    }
    throw new Error("Could not extract valid JSON from response");
  }
};

// Extract text from Anthropic-style content blocks.
// Response content can include "thinking" and "text" blocks - we want the "text" one.
const extractAnthropicText = (data) => {
  if (!data.content || !Array.isArray(data.content)) {
    debug.error("No content array in response. Keys:", Object.keys(data));
    throw new Error(
      `Unexpected response: no content array. Got keys: ${Object.keys(data).join(", ")}`,
    );
  }

  debug.log(
    "Content blocks:",
    data.content.map((b) => ({
      type: b.type,
      length: b.text?.length || b.thinking?.length || 0,
    })),
  );

  // Find the text block (skip thinking blocks)
  const textBlock = data.content.find((block) => block.type === "text");
  if (textBlock) {
    debug.log("Found text block, length:", textBlock.text.length);
    return textBlock.text;
  }

  // Fallback: if no typed blocks, try first block's text
  if (data.content[0]?.text) {
    debug.warn("No type=text block found, using content[0].text as fallback");
    return data.content[0].text;
  }

  debug.error(
    "Could not find text in content blocks:",
    JSON.stringify(data.content).substring(0, 500),
  );
  throw new Error(
    `No text content in response. Block types: ${data.content.map((b) => b.type).join(", ")}`,
  );
};

export { isTruncatedJSON, extractJSON, extractAnthropicText };
